import {
  type AnalysisTarget,
  getAnalysisTargetKey,
  parseAnalysisTarget,
  parseAnalysisTargets,
} from "@/lib/ai/analysis-target";

const ANALYSIS_SCOPE_DOMAINS = ["sa", "ua", "iw"] as const;
const ANALYSIS_SCOPE_KINDS = ["project", "view", "index_worker"] as const;

export type AnalysisScopeDomain = (typeof ANALYSIS_SCOPE_DOMAINS)[number];
export type AnalysisScopeKind = (typeof ANALYSIS_SCOPE_KINDS)[number];

export type AnalysisScopeRef = {
  refId: string;
  kind: AnalysisScopeKind;
  domain: AnalysisScopeDomain;
  ids: {
    projectId: string;
    entityId: string;
  };
  names: {
    project: string;
    entity: string | null;
    display: string;
  };
};

type AnalysisScopeMentionBinding = {
  raw: string;
  normalized: string;
  refId: string;
};

export type AnalysisScope = {
  refs: AnalysisScopeRef[];
  primaryRefId: string | null;
  pageRefId: string | null;
  pageContextEnabled: boolean;
  manualRefIds: string[];
  mentionedRefIds: string[];
  mentionBindings: AnalysisScopeMentionBinding[];
};

export type AnalysisScopeMentionInput = {
  raw: string;
  normalized: string;
  refId: string;
};

const EMPTY_ANALYSIS_SCOPE: AnalysisScope = {
  refs: [],
  primaryRefId: null,
  pageRefId: null,
  pageContextEnabled: false,
  manualRefIds: [],
  mentionedRefIds: [],
  mentionBindings: [],
};

/**
 * relation 情報から request-time の primary ref を決める。
 *
 * 契約:
 * - 最新 prompt の inline `@` mention があれば、その先頭 ref を最優先にする。
 * - 明示 mention が無いときだけ、enabled な page chip を既定優先にする。
 * - page context も無い場合は manual ref を fallback として使う。
 */
export function resolveAnalysisScopePrimaryRefId(params: {
  mentionedRefIds: string[];
  pageRefId: string | null;
  pageContextEnabled: boolean;
  manualRefIds: string[];
}): string | null {
  return (
    params.mentionedRefIds[0] ??
    (params.pageContextEnabled ? params.pageRefId : null) ??
    params.manualRefIds[0] ??
    null
  );
}

function parseNonEmptyString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`${label} must be a non-empty string.`);
  }
  return value;
}

function parseNullableString(value: unknown, label: string): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  return parseNonEmptyString(value, label);
}

function parseAnalysisScopeDomain(value: unknown): AnalysisScopeDomain {
  if (
    typeof value !== "string" ||
    !ANALYSIS_SCOPE_DOMAINS.includes(value as AnalysisScopeDomain)
  ) {
    throw new Error("analysisScope.refs[].domain must be one of sa, ua, iw.");
  }
  return value as AnalysisScopeDomain;
}

function parseAnalysisScopeKind(value: unknown): AnalysisScopeKind {
  if (
    typeof value !== "string" ||
    !ANALYSIS_SCOPE_KINDS.includes(value as AnalysisScopeKind)
  ) {
    throw new Error(
      "analysisScope.refs[].kind must be one of project, view, index_worker.",
    );
  }
  return value as AnalysisScopeKind;
}

function getDomainFromProjectType(
  projectType: AnalysisTarget["projectType"],
): AnalysisScopeDomain {
  if (projectType === "SearchAnalyticsProject") {
    return "sa";
  }
  if (projectType === "UserAnalyticsProject") {
    return "ua";
  }
  return "iw";
}

function getProjectTypeFromDomain(
  domain: AnalysisScopeDomain,
): AnalysisTarget["projectType"] {
  if (domain === "sa") {
    return "SearchAnalyticsProject";
  }
  if (domain === "ua") {
    return "UserAnalyticsProject";
  }
  return "IndexWorkerProject";
}

function getRefKindFromTarget(target: AnalysisTarget): AnalysisScopeKind {
  if (target.iwId) {
    return "index_worker";
  }
  if (target.viewId) {
    return "view";
  }
  return "project";
}

/**
 * AnalysisTarget の entity 単位で安定した `refId` を生成する。
 *
 * 契約:
 * - 同一ターゲットは常に同じ refId になる。
 * - project/view/index_worker のいずれも `getAnalysisTargetKey` を基準とする。
 */
function getAnalysisScopeRefId(target: AnalysisTarget): string {
  return getAnalysisTargetKey(target);
}

/**
 * AnalysisTarget を共通スキーマの AnalysisScopeRef へ変換する。
 *
 * 契約:
 * - ref は entity registry なので page/manual の relation 情報は持たない。
 * - `kind/domain` は target から自動決定する。
 * - `refId` 未指定時は `getAnalysisScopeRefId` を使用する。
 */
export function analysisTargetToAnalysisScopeRef(
  target: AnalysisTarget,
  options: { refId?: string } = {},
): AnalysisScopeRef {
  const kind = getRefKindFromTarget(target);
  const domain = getDomainFromProjectType(target.projectType);
  const entityId = target.iwId ?? target.viewId ?? target.projectId;
  const entityName = target.iwName ?? target.viewName ?? null;
  const display = entityName
    ? `${target.projectName} / ${entityName}`
    : target.projectName;

  return {
    refId: options.refId ?? getAnalysisScopeRefId(target),
    kind,
    domain,
    ids: {
      projectId: target.projectId,
      entityId,
    },
    names: {
      project: target.projectName,
      entity: entityName,
      display,
    },
  } satisfies AnalysisScopeRef;
}

/**
 * AnalysisScopeRef を AnalysisTarget へ復元する。
 *
 * 失敗時:
 * - kind/domain の矛盾や必須ID不足は例外を投げる。
 */
export function analysisScopeRefToAnalysisTarget(
  ref: AnalysisScopeRef,
): AnalysisTarget {
  const projectType = getProjectTypeFromDomain(ref.domain);
  const baseTarget: AnalysisTarget = {
    projectId: ref.ids.projectId,
    projectName: ref.names.project,
    projectType,
  };

  if (ref.kind === "project") {
    if (ref.ids.entityId !== ref.ids.projectId) {
      throw new Error(
        "analysisScope.refs[].ids.entityId must equal projectId for project kind.",
      );
    }
    return baseTarget;
  }

  if (ref.kind === "view") {
    if (ref.domain === "iw") {
      throw new Error("analysisScope view kind cannot use iw domain.");
    }
    return {
      ...baseTarget,
      viewId: ref.ids.entityId,
      viewName: ref.names.entity ?? ref.names.display,
    };
  }

  if (ref.domain !== "iw") {
    throw new Error("analysisScope index_worker kind must use iw domain.");
  }

  return {
    ...baseTarget,
    iwId: ref.ids.entityId,
    iwName: ref.names.entity ?? ref.names.display,
  };
}

/**
 * page/manual relation とメンション束縛から送信用 analysisScope を構築する。
 *
 * 契約:
 * - `refs` は entity registry として refId ごとに一意化する。
 * - `pageTarget` は page chip として表示中の target を表す。
 * - page/manual relation は `pageRefId` / `manualRefIds` で別管理する。
 * - `mentionedRefIds` は最新入力で明示 mention された secondary scope を表す。
 * - primary precedence は `mention -> enabled page -> manual` とする。
 * - inline mention だけで指定された target も `mentionedTargets` から registry へ追加する。
 * - 同一 refId が page/manual の両方に現れても reject せず relation として保持する。
 */
export function buildAnalysisScope(params: {
  pageTarget: AnalysisTarget | null;
  pageContextEnabled?: boolean;
  manualTargets: AnalysisTarget[];
  mentionedTargets?: AnalysisTarget[];
  mentionInputs?: AnalysisScopeMentionInput[];
}): AnalysisScope {
  const pageTarget = parseAnalysisTarget(params.pageTarget);
  const pageContextEnabled =
    params.pageContextEnabled === undefined
      ? pageTarget !== null
      : Boolean(params.pageContextEnabled && pageTarget);
  const manualTargets = parseAnalysisTargets(params.manualTargets);
  const mentionedTargets = parseAnalysisTargets(params.mentionedTargets ?? []);

  const refs: AnalysisScopeRef[] = [];
  const refById = new Map<string, AnalysisScopeRef>();
  const manualRefIds: string[] = [];
  const mentionedRefIds: string[] = [];
  const appendRefIfMissing = (target: AnalysisTarget) => {
    const ref = analysisTargetToAnalysisScopeRef(target);
    if (refById.has(ref.refId)) {
      return ref.refId;
    }
    refById.set(ref.refId, ref);
    refs.push(ref);
    return ref.refId;
  };

  for (const target of manualTargets) {
    const refId = appendRefIfMissing(target);
    if (manualRefIds.includes(refId)) {
      continue;
    }
    manualRefIds.push(refId);
  }

  for (const target of mentionedTargets) {
    const refId = appendRefIfMissing(target);
    if (mentionedRefIds.includes(refId)) {
      continue;
    }
    mentionedRefIds.push(refId);
  }

  const pageRefId = pageTarget ? appendRefIfMissing(pageTarget) : null;
  const primaryRefId = resolveAnalysisScopePrimaryRefId({
    mentionedRefIds,
    pageRefId,
    pageContextEnabled,
    manualRefIds,
  });
  const mentionInputs = params.mentionInputs ?? [];
  const mentionBindings: AnalysisScopeMentionBinding[] = [];

  for (const input of mentionInputs) {
    if (!refById.has(input.refId)) {
      continue;
    }
    const raw = input.raw.trim();
    const normalized = input.normalized.trim();
    if (!raw || !normalized) {
      continue;
    }
    mentionBindings.push({
      raw,
      normalized,
      refId: input.refId,
    });
  }

  return {
    refs,
    primaryRefId,
    pageRefId,
    pageContextEnabled,
    manualRefIds,
    mentionedRefIds,
    mentionBindings,
  } satisfies AnalysisScope;
}

function parseAnalysisScopeRef(raw: unknown, index: number): AnalysisScopeRef {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    throw new Error(`analysisScope.refs[${index}] must be an object.`);
  }

  const record = raw as Record<string, unknown>;
  const ids = record.ids;
  const names = record.names;

  if (typeof ids !== "object" || ids === null || Array.isArray(ids)) {
    throw new Error(`analysisScope.refs[${index}].ids must be an object.`);
  }
  if (typeof names !== "object" || names === null || Array.isArray(names)) {
    throw new Error(`analysisScope.refs[${index}].names must be an object.`);
  }

  const idsRecord = ids as Record<string, unknown>;
  const namesRecord = names as Record<string, unknown>;
  const rawEntityName = namesRecord.entity;

  return {
    refId: parseNonEmptyString(
      record.refId,
      `analysisScope.refs[${index}].refId`,
    ),
    kind: parseAnalysisScopeKind(record.kind),
    domain: parseAnalysisScopeDomain(record.domain),
    ids: {
      projectId: parseNonEmptyString(
        idsRecord.projectId,
        `analysisScope.refs[${index}].ids.projectId`,
      ),
      entityId: parseNonEmptyString(
        idsRecord.entityId,
        `analysisScope.refs[${index}].ids.entityId`,
      ),
    },
    names: {
      project: parseNonEmptyString(
        namesRecord.project,
        `analysisScope.refs[${index}].names.project`,
      ),
      entity:
        rawEntityName === null || rawEntityName === undefined
          ? null
          : parseNonEmptyString(
              rawEntityName,
              `analysisScope.refs[${index}].names.entity`,
            ),
      display: parseNonEmptyString(
        namesRecord.display,
        `analysisScope.refs[${index}].names.display`,
      ),
    },
  } satisfies AnalysisScopeRef;
}

function parseMentionBinding(
  raw: unknown,
  index: number,
): AnalysisScopeMentionBinding {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    throw new Error(
      `analysisScope.mentionBindings[${index}] must be an object.`,
    );
  }
  const record = raw as Record<string, unknown>;
  return {
    raw: parseNonEmptyString(
      record.raw,
      `analysisScope.mentionBindings[${index}].raw`,
    ),
    normalized: parseNonEmptyString(
      record.normalized,
      `analysisScope.mentionBindings[${index}].normalized`,
    ),
    refId: parseNonEmptyString(
      record.refId,
      `analysisScope.mentionBindings[${index}].refId`,
    ),
  } satisfies AnalysisScopeMentionBinding;
}

/**
 * リクエストボディの analysisScope を厳格に検証し、正規化する。
 *
 * 契約:
 * - `null`/`undefined` は空スコープとして扱う。
 * - refId / pageRefId / manualRefIds / mentionedRefIds / mentionBindings は
 *   必ず refs と整合する。
 * - `pageContextEnabled=true` のときは `pageRefId` が必須。
 * - `pageRefId` と `manualRefIds` が同じ refId を指すことは許容する。
 *
 * 失敗時:
 * - 形式不正や参照不整合は例外を投げ、呼び出し元で 400 を返す。
 */
export function parseAnalysisScope(raw: unknown): AnalysisScope {
  if (raw === null || raw === undefined) {
    return {
      ...EMPTY_ANALYSIS_SCOPE,
      refs: [],
      manualRefIds: [],
      mentionedRefIds: [],
      mentionBindings: [],
    };
  }

  if (typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error("analysisScope must be an object.");
  }

  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.refs)) {
    throw new Error("analysisScope.refs must be an array.");
  }
  if (!Array.isArray(record.manualRefIds)) {
    throw new Error("analysisScope.manualRefIds must be an array.");
  }
  if (!Array.isArray(record.mentionedRefIds)) {
    throw new Error("analysisScope.mentionedRefIds must be an array.");
  }
  if (typeof record.pageContextEnabled !== "boolean") {
    throw new Error("analysisScope.pageContextEnabled must be a boolean.");
  }
  if (!Array.isArray(record.mentionBindings)) {
    throw new Error("analysisScope.mentionBindings must be an array.");
  }

  const refs = record.refs.map((item, index) =>
    parseAnalysisScopeRef(item, index),
  );
  const refIdSet = new Set<string>();
  refs.forEach((ref) => {
    if (refIdSet.has(ref.refId)) {
      throw new Error("analysisScope.refs.refId must be unique.");
    }
    refIdSet.add(ref.refId);

    // kind/domain の矛盾を早期に検出する。
    if (ref.kind === "view" && ref.domain === "iw") {
      throw new Error("analysisScope view kind cannot use iw domain.");
    }
    if (ref.kind === "index_worker" && ref.domain !== "iw") {
      throw new Error("analysisScope index_worker kind must use iw domain.");
    }
    if (ref.kind === "project" && ref.ids.entityId !== ref.ids.projectId) {
      throw new Error(
        "analysisScope project kind requires ids.entityId to equal projectId.",
      );
    }
  });

  const pageRefId = parseNullableString(
    record.pageRefId,
    "analysisScope.pageRefId",
  );
  if (pageRefId && !refIdSet.has(pageRefId)) {
    throw new Error("analysisScope.pageRefId must reference an existing ref.");
  }
  const pageContextEnabled = record.pageContextEnabled;
  if (pageContextEnabled && pageRefId === null) {
    throw new Error(
      "analysisScope.pageContextEnabled requires pageRefId to be present.",
    );
  }

  const manualRefIds = (record.manualRefIds as unknown[]).map((value, index) =>
    parseNonEmptyString(value, `analysisScope.manualRefIds[${index}]`),
  );
  const manualRefIdSet = new Set<string>();
  manualRefIds.forEach((refId) => {
    if (manualRefIdSet.has(refId)) {
      throw new Error(
        "analysisScope.manualRefIds must not contain duplicates.",
      );
    }
    if (!refIdSet.has(refId)) {
      throw new Error(
        "analysisScope.manualRefIds must reference existing refs only.",
      );
    }
    manualRefIdSet.add(refId);
  });

  const primaryRefId = parseNullableString(
    record.primaryRefId,
    "analysisScope.primaryRefId",
  );
  if (primaryRefId && !refIdSet.has(primaryRefId)) {
    throw new Error(
      "analysisScope.primaryRefId must reference an existing ref.",
    );
  }

  const mentionedRefIds = (record.mentionedRefIds as unknown[]).map(
    (value, index) =>
      parseNonEmptyString(value, `analysisScope.mentionedRefIds[${index}]`),
  );
  const mentionedRefIdSet = new Set<string>();
  mentionedRefIds.forEach((refId) => {
    if (mentionedRefIdSet.has(refId)) {
      throw new Error(
        "analysisScope.mentionedRefIds must not contain duplicates.",
      );
    }
    if (!refIdSet.has(refId)) {
      throw new Error(
        "analysisScope.mentionedRefIds must reference existing refs only.",
      );
    }
    mentionedRefIdSet.add(refId);
  });

  const mentionBindings = (record.mentionBindings as unknown[]).map(
    (binding, index) => parseMentionBinding(binding, index),
  );
  mentionBindings.forEach((binding) => {
    if (!refIdSet.has(binding.refId)) {
      throw new Error(
        "analysisScope.mentionBindings[].refId must reference an existing ref.",
      );
    }
  });

  return {
    refs,
    primaryRefId,
    pageRefId,
    pageContextEnabled,
    manualRefIds,
    mentionedRefIds,
    mentionBindings,
  } satisfies AnalysisScope;
}
